import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import AppSidebar from '../components/AppSidebar';
import CashFlowProformaTable from '../components/dashboards/CashFlowProformaTable';
import WtiLoader from '../components/WtiLoader';
import {
  offerPlaygroundService,
  OfferPlayground,
  OfferScenario,
  ScenarioInputs,
  ExpenseBasis,
  ExpenseInput,
  LoanType,
} from '../services/offerPlaygroundService';
import '../App.css';

const money = (n: number | null | undefined) =>
  n != null && Number.isFinite(Number(n)) ? `$${Math.round(Number(n)).toLocaleString()}` : '—';

const pct = (n: number | null | undefined) =>
  n != null && Number.isFinite(Number(n)) ? `${Number(n).toFixed(2)}%` : '—';

const numInput = (v: string) => {
  const n = parseFloat(v.replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
};

const LOAN_TYPE_OPTIONS: { value: LoanType; label: string }[] = [
  { value: 'FULLY_AMORTIZING', label: 'Fully Amortizing' },
  { value: 'INTEREST_ONLY', label: 'Interest-Only' },
];

const EXPENSE_BASIS_OPTIONS: { value: ExpenseBasis; label: string }[] = [
  { value: 'PERCENT_RENT', label: '% of rent' },
  { value: 'FIXED_MONTHLY', label: '$ / month' },
  { value: 'FIXED_ANNUAL', label: '$ / year' },
  { value: 'PER_UNIT_MONTHLY', label: '$ / unit / month' },
  { value: 'PERCENT_PROPERTY_VALUE', label: '% of property value' },
  { value: 'PERCENT_PURCHASE_PRICE', label: '% of purchase price' },
];

const OPERATING_EXPENSE_FIELDS: Array<{
  key: string;
  label: string;
  defaultBasis: ExpenseBasis;
  defaultValue: number;
}> = [
  { key: 'taxes', label: 'Taxes', defaultBasis: 'FIXED_ANNUAL', defaultValue: 0 },
  { key: 'insurance', label: 'Insurance', defaultBasis: 'PERCENT_PROPERTY_VALUE', defaultValue: 0.6 },
  { key: 'maintenance', label: 'Maintenance', defaultBasis: 'PER_UNIT_MONTHLY', defaultValue: 50 },
  { key: 'management', label: 'Management', defaultBasis: 'PERCENT_RENT', defaultValue: 10 },
  { key: 'utilities', label: 'Utilities', defaultBasis: 'FIXED_MONTHLY', defaultValue: 45 },
  { key: 'reserves', label: 'Reserves', defaultBasis: 'PERCENT_RENT', defaultValue: 5 },
  { key: 'vacancy', label: 'Vacancy', defaultBasis: 'PERCENT_RENT', defaultValue: 5 },
];

const moneyPerMonthYear = (monthly: number | null | undefined, annual: number | null | undefined) => {
  if (monthly == null && annual == null) return '—';
  const m = monthly != null ? money(monthly) : money((annual || 0) / 12);
  const y = annual != null ? money(annual) : money((monthly || 0) * 12);
  return `${m}/mo · ${y}/yr`;
};

/** Mirrors backend offerPlaygroundCalc.resolveAmount for optimistic UI updates. */
const resolveExpenseAnnual = (
  config: { value?: number | null; basis?: string | null },
  ctx: { purchasePrice: number; annualRent: number; units: number },
): number => {
  if (!config || config.value == null) return 0;
  const val = Number(config.value);
  if (!Number.isFinite(val)) return 0;
  const units = Math.max(1, ctx.units || 1);
  switch (config.basis) {
    case 'FIXED_MONTHLY':
      return Math.round(val * 12);
    case 'FIXED_ANNUAL':
      return Math.round(val);
    case 'PERCENT_RENT':
      return Math.round(ctx.annualRent * (val / 100));
    case 'PER_UNIT_MONTHLY':
      return Math.round(val * units * 12);
    case 'PERCENT_PROPERTY_VALUE':
    case 'PERCENT_PURCHASE_PRICE':
      return Math.round(ctx.purchasePrice * (val / 100));
    default:
      return Math.round(val);
  }
};

/** Mirrors backend resolveLoanAmountConfig for optimistic UI updates. */
const resolveLoanDollars = (
  config: { value?: number | null; basis?: string | null } | null | undefined,
  purchasePrice: number,
  fallbackPct = 0,
): number => {
  if (!config) return Math.round(purchasePrice * (fallbackPct / 100));
  const raw = Number(config.value);
  const val = Number.isFinite(raw) ? raw : fallbackPct;
  if (config.basis === 'PERCENT_PURCHASE_PRICE' || config.basis === 'PERCENT_PROPERTY_VALUE') {
    return Math.round(purchasePrice * (val / 100));
  }
  return Math.round(val);
};

const withOptimisticScenario = (
  scenario: OfferScenario,
  nextInputs: ScenarioInputs,
): OfferScenario => {
  const cf = scenario.cashFlowStatement;
  if (!cf) return { ...scenario, inputs: nextInputs };

  const monthlyRent =
    (Number(nextInputs.monthlyRent) || 0) + (Number(nextInputs.monthlyOtherIncome) || 0);
  const annualRent = Math.round(monthlyRent * 12);
  const purchasePrice = Number(nextInputs.purchasePrice) || cf.purchasePrice || 0;
  const units = Math.max(1, Number(nextInputs.units) || cf.units || 1);
  const ctx = { purchasePrice, annualRent, units };

  const expenseLines = OPERATING_EXPENSE_FIELDS.map(({ key, label }) => {
    const config = nextInputs.expenses?.[key];
    const prior = cf.expenseLines?.find((l) => l.key === key);
    const annual = config ? resolveExpenseAnnual(config, ctx) : prior?.annual ?? 0;
    return { key, label, annual, monthly: Math.round(annual / 12) };
  });

  for (const custom of nextInputs.customExpenses || []) {
    const label = String(custom.label || '').trim() || 'Custom expense';
    const annual = resolveExpenseAnnual(custom, ctx);
    expenseLines.push({
      key: `custom_${custom.id || label}`,
      label,
      annual,
      monthly: Math.round(annual / 12),
    });
  }

  const operatingAnnual = expenseLines.reduce((sum, line) => sum + line.annual, 0);
  const noiAnnual = annualRent - operatingAnnual;

  const downPayment = resolveLoanDollars(
    nextInputs.primaryFinancing?.downPayment,
    purchasePrice,
    20,
  );
  const primaryLoanAmount = Math.max(0, Math.round(purchasePrice - downPayment));
  const secondaryEnabled = !!nextInputs.secondaryFinancing?.enabled;
  const secondaryLoanAmount = secondaryEnabled
    ? Math.min(
        Math.max(0, downPayment),
        Math.max(
          0,
          resolveLoanDollars(nextInputs.secondaryFinancing?.loanAmount, purchasePrice, 0),
        ),
      )
    : 0;
  const cashDown = Math.max(0, Math.round(downPayment - secondaryLoanAmount));
  const closingCosts = resolveExpenseAnnual(
    nextInputs.closingCosts || { value: 0, basis: 'FIXED_ANNUAL' },
    { purchasePrice, annualRent: 0, units: 1 },
  );
  const acquisitionTotal =
    Math.round(Number(nextInputs.immediateRepairs) || 0) +
    closingCosts +
    Math.round(Number(nextInputs.otherAcquisitionCosts) || 0);
  const cashRequired = Math.max(0, cashDown + acquisitionTotal);

  const debtAnnual =
    cf.totalDebtServiceAnnual ??
    Math.round((cf.mortgage?.monthlyPayment || 0) * 12) +
      Math.round((cf.secondaryMortgage?.monthlyPayment || 0) * 12);
  const cashFlowAnnual = noiAnnual - debtAnnual;
  const noiMonthly = Math.round(noiAnnual / 12);
  const cashFlowMonthly = Math.round(cashFlowAnnual / 12);
  const cashOnCashReturn =
    cashRequired > 0
      ? parseFloat(((cashFlowAnnual / cashRequired) * 100).toFixed(2))
      : null;

  return {
    ...scenario,
    inputs: nextInputs,
    cashFlowStatement: {
      ...cf,
      purchasePrice: Math.round(purchasePrice),
      monthlyRent: Math.round(monthlyRent),
      annualRent,
      units,
      expenseLines,
      noiAnnual,
      noiMonthly,
      cashFlowAnnual,
      cashFlowMonthly,
      cashOnCashReturn,
      mortgage: cf.mortgage
        ? {
            ...cf.mortgage,
            downPaymentPct:
              purchasePrice > 0 ? Math.round((downPayment / purchasePrice) * 1000) / 10 : 0,
            loanAmount: primaryLoanAmount,
          }
        : cf.mortgage,
      secondaryMortgage:
        secondaryEnabled && secondaryLoanAmount > 0
          ? {
              ...(cf.secondaryMortgage || {
                interestRate: nextInputs.secondaryFinancing?.interestRate ?? 8,
                termYears: nextInputs.secondaryFinancing?.termYears ?? 10,
                monthlyPayment: 0,
                loanType: nextInputs.secondaryFinancing?.loanType || 'FULLY_AMORTIZING',
              }),
              label: nextInputs.secondaryFinancing?.label || 'Secondary financing',
              loanAmount: secondaryLoanAmount,
            }
          : null,
    },
    results: scenario.results
      ? {
          ...scenario.results,
          noiAnnual,
          noiMonthly,
          cashFlowAnnual,
          cashFlowMonthly,
          annualRent,
          operatingExpensesAnnual: operatingAnnual,
          cashRequired,
          cashOnCashReturn,
          acquisitionCosts: acquisitionTotal,
        }
      : scenario.results,
  };
};

const metricTone = (value: number | null | undefined): 'pos' | 'neg' | 'neutral' => {
  if (value == null || !Number.isFinite(Number(value))) return 'neutral';
  if (Number(value) > 0) return 'pos';
  if (Number(value) < 0) return 'neg';
  return 'neutral';
};

const Field: React.FC<{
  label: string;
  children: React.ReactNode;
  hint?: React.ReactNode;
  wide?: boolean;
}> = ({ label, children, hint, wide }) => (
  <label className={`op-field${wide ? ' op-field--wide' : ''}`}>
    <span className="op-field__label">{label}</span>
    {children}
    {hint}
  </label>
);

const OfferPlaygroundPage: React.FC = () => {
  const { playgroundId } = useParams<{ playgroundId: string }>();
  const navigate = useNavigate();
  const [playground, setPlayground] = useState<OfferPlayground | null>(null);
  const [activeScenarioId, setActiveScenarioId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [compareMode, setCompareMode] = useState(false);
  const [compareIds, setCompareIds] = useState<string[]>([]);
  const [compareRows, setCompareRows] = useState<OfferScenario[]>([]);
  const [editingScenarioId, setEditingScenarioId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const renameInputRef = useRef<HTMLInputElement | null>(null);
  const saveSeq = useRef(0);
  const pendingSave = useRef<{ scenarioId: string; inputs: ScenarioInputs; seq: number } | null>(
    null,
  );
  const saveInFlight = useRef(false);

  const activeScenario = useMemo(
    () => playground?.scenarios.find((s) => s.id === activeScenarioId) || null,
    [playground, activeScenarioId],
  );

  const load = useCallback(async () => {
    if (!playgroundId) return;
    setLoading(true);
    try {
      const data = await offerPlaygroundService.get(playgroundId);
      setPlayground(data);
      const preferred =
        data.scenarios.find((s) => s.name === 'My Offer' && !s.isBaseline) ||
        data.scenarios.find((s) => !s.isBaseline) ||
        data.scenarios[0];
      setActiveScenarioId((prev) =>
        prev && data.scenarios.some((s) => s.id === prev) ? prev : preferred?.id || null,
      );
    } catch {
      toast.error('Failed to load Offer Playground');
    } finally {
      setLoading(false);
    }
  }, [playgroundId]);

  useEffect(() => {
    load();
  }, [load]);

  const flushSave = useCallback(async () => {
    if (saveInFlight.current) return;
    const job = pendingSave.current;
    if (!job) return;
    pendingSave.current = null;
    saveInFlight.current = true;
    setSaveState('saving');

    const inputsJson: ScenarioInputs = {
      ...job.inputs,
      customExpenses: (job.inputs.customExpenses || []).map((row, index) => ({
        ...row,
        id: row.id || `custom_${index}_${Date.now()}`,
        label: String(row.label || '').trim() || 'Custom expense',
      })),
    };

    try {
      const updated = await offerPlaygroundService.updateScenario(job.scenarioId, {
        inputsJson,
      });
      // Only apply if this response is still the latest edit (avoids wiping newer custom expenses).
      if (job.seq === saveSeq.current) {
        setPlayground((pg) =>
          pg
            ? {
                ...pg,
                scenarios: pg.scenarios.map((s) =>
                  s.id === updated.id ? { ...s, ...updated } : s,
                ),
              }
            : pg,
        );
        setSaveState('saved');
      }
    } catch {
      if (job.seq === saveSeq.current) {
        setSaveState('error');
        toast.error('Unable to save — retry');
      }
    } finally {
      saveInFlight.current = false;
      if (pendingSave.current) {
        void flushSave();
      }
    }
  }, []);

  const queueSave = useCallback(
    (scenarioId: string, inputs: ScenarioInputs, opts?: { immediate?: boolean }) => {
      const seq = ++saveSeq.current;
      pendingSave.current = { scenarioId, inputs, seq };
      if (saveTimer.current) clearTimeout(saveTimer.current);
      if (opts?.immediate) {
        void flushSave();
        return;
      }
      saveTimer.current = setTimeout(() => {
        void flushSave();
      }, 850);
    },
    [flushSave],
  );

  const patchInputs = (patch: Partial<ScenarioInputs>, opts?: { immediate?: boolean }) => {
    if (!activeScenario || activeScenario.isBaseline) return;
    const next = { ...activeScenario.inputs, ...patch };
    setPlayground((pg) =>
      pg
        ? {
            ...pg,
            scenarios: pg.scenarios.map((s) =>
              s.id === activeScenario.id ? withOptimisticScenario(s, next) : s,
            ),
          }
        : pg,
    );
    queueSave(activeScenario.id, next, opts);
  };

  const getExpenseConfig = (key: string): ExpenseInput => {
    const field = OPERATING_EXPENSE_FIELDS.find((f) => f.key === key);
    const existing = activeScenario?.inputs.expenses?.[key];
    if (existing) return existing;
    const line = activeScenario?.cashFlowStatement?.expenseLines?.find((l) => l.key === key);
    if (line) return { value: line.annual, basis: 'FIXED_ANNUAL' };
    return {
      value: field?.defaultValue ?? 0,
      basis: field?.defaultBasis ?? 'FIXED_ANNUAL',
    };
  };

  const patchExpense = (key: string, patch: Partial<ExpenseInput>) => {
    if (!activeScenario || activeScenario.isBaseline) return;
    const nextExpenses: Record<string, ExpenseInput> = {};
    for (const { key: expenseKey } of OPERATING_EXPENSE_FIELDS) {
      nextExpenses[expenseKey] =
        activeScenario.inputs.expenses?.[expenseKey] || getExpenseConfig(expenseKey);
    }
    nextExpenses[key] = { ...nextExpenses[key], ...patch };
    patchInputs({ expenses: nextExpenses });
  };

  const customExpenses = activeScenario?.inputs.customExpenses || [];

  const patchCustomExpense = (
    id: string,
    patch: Partial<ExpenseInput & { label: string }>,
  ) => {
    if (!activeScenario || activeScenario.isBaseline) return;
    const next = customExpenses.map((row) => {
      const rowKey = row.id || row.label;
      return rowKey === id ? { ...row, id: row.id || id, ...patch } : row;
    });
    patchInputs({ customExpenses: next });
  };

  const addCustomExpense = () => {
    if (!activeScenario || activeScenario.isBaseline) return;
    const id =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `custom_${Date.now()}`;
    patchInputs(
      {
        customExpenses: [
          ...customExpenses,
          { id, label: 'Custom expense', value: 0, basis: 'FIXED_MONTHLY' },
        ],
      },
      { immediate: true },
    );
  };

  const removeCustomExpense = (id: string) => {
    if (!activeScenario || activeScenario.isBaseline) return;
    patchInputs(
      {
        customExpenses: customExpenses.filter((row) => (row.id || row.label) !== id),
      },
      { immediate: true },
    );
  };

  const patchSecondaryFinancing = (
    patch: Partial<NonNullable<ScenarioInputs['secondaryFinancing']>>,
  ) => {
    if (!activeScenario || activeScenario.isBaseline) return;
    const current = activeScenario.inputs.secondaryFinancing || {
      enabled: false,
      label: 'Secondary financing',
      loanAmount: { value: 0, basis: 'PERCENT_PURCHASE_PRICE' as ExpenseBasis },
      interestRate: 8,
      termYears: 10,
      loanType: 'FULLY_AMORTIZING' as LoanType,
    };
    const baseLoanAmount = current.loanAmount || {
      value: 0,
      basis: 'PERCENT_PURCHASE_PRICE' as ExpenseBasis,
    };
    patchInputs({
      secondaryFinancing: {
        ...current,
        ...patch,
        loanAmount: patch.loanAmount
          ? { ...baseLoanAmount, ...patch.loanAmount }
          : baseLoanAmount,
      },
    });
  };

  const patchPrimaryFinancing = (
    patch: Partial<NonNullable<ScenarioInputs['primaryFinancing']>>,
  ) => {
    if (!activeScenario || activeScenario.isBaseline) return;
    const current = activeScenario.inputs.primaryFinancing || {
      downPayment: { value: 20, basis: 'PERCENT_PURCHASE_PRICE' as ExpenseBasis },
      interestRate: 7,
      termYears: 30,
      loanType: 'FULLY_AMORTIZING' as LoanType,
    };
    patchInputs({
      primaryFinancing: {
        ...current,
        ...patch,
        downPayment: patch.downPayment
          ? { ...current.downPayment, ...patch.downPayment }
          : current.downPayment,
      },
    });
  };

  const startRenameScenario = (scenario: OfferScenario, e: React.MouseEvent) => {
    if (scenario.isBaseline) return;
    e.stopPropagation();
    setEditingScenarioId(scenario.id);
    setEditingName(scenario.name);
  };

  const cancelRenameScenario = () => {
    setEditingScenarioId(null);
    setEditingName('');
  };

  const commitRenameScenario = useCallback(
    async (scenarioId: string) => {
      const trimmed = editingName.trim();
      setEditingScenarioId(null);
      setEditingName('');

      if (!trimmed || !playground) return;
      const existing = playground.scenarios.find((s) => s.id === scenarioId);
      if (!existing || existing.isBaseline || trimmed === existing.name) return;

      setPlayground((pg) =>
        pg
          ? {
              ...pg,
              scenarios: pg.scenarios.map((s) =>
                s.id === scenarioId ? { ...s, name: trimmed } : s,
              ),
            }
          : pg,
      );
      setSaveState('saving');
      try {
        const updated = await offerPlaygroundService.updateScenario(scenarioId, { name: trimmed });
        setPlayground((pg) =>
          pg
            ? {
                ...pg,
                scenarios: pg.scenarios.map((s) => (s.id === updated.id ? { ...s, ...updated } : s)),
              }
            : pg,
        );
        setSaveState('saved');
      } catch {
        setSaveState('error');
        toast.error('Unable to rename scenario');
        load();
      }
    },
    [editingName, load, playground],
  );

  useEffect(() => {
    if (editingScenarioId) renameInputRef.current?.focus();
  }, [editingScenarioId]);

  const handleAddScenario = async () => {
    if (!playground) return;
    try {
      const created = await offerPlaygroundService.addScenario(playground.id);
      setPlayground((pg) => (pg ? { ...pg, scenarios: [...pg.scenarios, created] } : pg));
      setActiveScenarioId(created.id);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Could not add scenario');
    }
  };

  const handleDuplicateScenario = async () => {
    if (!activeScenario || activeScenario.isBaseline) return;
    try {
      const copy = await offerPlaygroundService.duplicateScenario(activeScenario.id);
      setPlayground((pg) => (pg ? { ...pg, scenarios: [...pg.scenarios, copy] } : pg));
      setActiveScenarioId(copy.id);
    } catch {
      toast.error('Duplicate failed');
    }
  };

  const handleDeleteScenario = async () => {
    if (!activeScenario || activeScenario.isBaseline) return;
    if (!window.confirm(`Delete scenario "${activeScenario.name}"?`)) return;
    try {
      await offerPlaygroundService.deleteScenario(activeScenario.id);
      setPlayground((pg) => {
        if (!pg) return pg;
        const remaining = pg.scenarios.filter((s) => s.id !== activeScenario.id);
        setActiveScenarioId(remaining.find((s) => !s.isBaseline)?.id || remaining[0]?.id || null);
        return { ...pg, scenarios: remaining };
      });
    } catch {
      toast.error('Delete failed');
    }
  };

  const handleResetScenario = async () => {
    if (!activeScenario || activeScenario.isBaseline) return;
    if (!window.confirm('Reset entire scenario to WTI Baseline?')) return;
    try {
      const updated = await offerPlaygroundService.resetScenario(activeScenario.id);
      setPlayground((pg) =>
        pg
          ? {
              ...pg,
              scenarios: pg.scenarios.map((s) => (s.id === updated.id ? { ...s, ...updated } : s)),
            }
          : pg,
      );
      toast.success('Reset to WTI Baseline');
    } catch {
      toast.error('Reset failed');
    }
  };

  const runCompare = async () => {
    if (!playground || compareIds.length === 0) return;
    try {
      const rows = await offerPlaygroundService.compare(playground.id, compareIds.slice(0, 4));
      setCompareRows(rows);
      setCompareMode(true);
    } catch {
      toast.error('Compare failed');
    }
  };

  const toggleCompareId = (id: string) => {
    setCompareIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 4) return prev;
      return [...prev, id];
    });
  };

  if (loading || !playground) {
    return (
      <div className="wti-shell">
        <AppSidebar />
        <div className="wti-main op-page">
          {loading ? (
            <WtiLoader fullPage message="Loading Offer Playground…" />
          ) : (
            <div className="op-loading">Playground not found</div>
          )}
        </div>
      </div>
    );
  }

  const results = activeScenario?.results;
  const cf = activeScenario?.cashFlowStatement;
  const wtiValue = playground.wtiEstimatedValue;
  const offerPrice = activeScenario?.inputs?.purchasePrice;
  const pctVsWtiNum =
    wtiValue && offerPrice != null && Number(wtiValue) !== 0
      ? ((Number(offerPrice) - Number(wtiValue)) / Number(wtiValue)) * 100
      : null;
  const pctVsWti = pctVsWtiNum != null ? pctVsWtiNum.toFixed(1) : null;
  const readOnly = !!activeScenario?.isBaseline;

  const metrics: Array<{
    label: string;
    value: string;
    tone: 'pos' | 'neg' | 'neutral';
    accent?: boolean;
  }> = [
    { label: 'Gross yield', value: pct(results?.grossYield), tone: metricTone(results?.grossYield) },
    { label: 'Cap rate', value: pct(results?.capRate), tone: metricTone(results?.capRate) },
    { label: 'NOI (annual)', value: money(results?.noiAnnual), tone: metricTone(results?.noiAnnual) },
    {
      label: 'Cash flow (mo)',
      value: money(results?.cashFlowMonthly),
      tone: metricTone(results?.cashFlowMonthly),
      accent: true,
    },
    {
      label: 'Cash-on-cash',
      value: pct(results?.cashOnCashReturn),
      tone: metricTone(results?.cashOnCashReturn),
    },
    {
      label: 'DSCR',
      value: results?.dscr != null ? String(results.dscr) : '—',
      tone: results?.dscr != null && Number(results.dscr) >= 1 ? 'pos' : metricTone(results?.dscr),
    },
    { label: 'Break-even', value: pct(results?.breakEvenRatio), tone: 'neutral' },
    { label: 'Cash required', value: money(results?.cashRequired), tone: 'neutral' },
  ];

  return (
    <div className="wti-shell">
      <AppSidebar />
      <div className="wti-main op-page">
        <header className="op-hero">
          <div className="op-hero__top">
            <div>
              <div className="op-hero__eyebrow">
                <span className="op-hero__badge">Live model</span>
                Offer Playground
              </div>
              <h1 className="op-hero__title">{playground.address || playground.name}</h1>
              <div className="op-hero__meta">
                <span className="op-chip">{playground.propertyTypeLabel || playground.propertyType || '—'}</span>
                <span className="op-chip op-chip--soft">WTI value {money(playground.wtiEstimatedValue)}</span>
                <span className="op-chip op-chip--soft">WTI rent {money(playground.wtiEstimatedRent)}/mo</span>
                <span
                  className={`op-save${
                    saveState === 'saving'
                      ? ' op-save--saving'
                      : saveState === 'saved'
                        ? ' op-save--saved'
                        : saveState === 'error'
                          ? ' op-save--error'
                          : ''
                  }`}
                >
                  {saveState === 'saving' && 'Saving…'}
                  {saveState === 'saved' && 'Saved'}
                  {saveState === 'error' && 'Unable to save — Retry'}
                </span>
              </div>
            </div>
            <div className="op-hero__actions">
              <button
                type="button"
                className="op-btn op-btn--ghost"
                onClick={() => navigate(`/evaluation/${playground.reportId}`)}
              >
                ← Back to report
              </button>
              <button
                type="button"
                className="op-btn op-btn--primary"
                onClick={runCompare}
                disabled={compareIds.length === 0}
              >
                Compare ({compareIds.length}/4)
              </button>
            </div>
          </div>
        </header>

        <div className="op-tabs-bar">
          <div className="op-tabs">
            {playground.scenarios.map((s) => (
              <button
                key={s.id}
                type="button"
                className={`op-tab${s.id === activeScenarioId ? ' op-tab--active' : ''}${
                  s.isBaseline ? ' op-tab--locked' : ''
                }${editingScenarioId === s.id ? ' op-tab--editing' : ''}`}
                onClick={() => {
                  if (editingScenarioId === s.id) return;
                  setActiveScenarioId(s.id);
                  setCompareMode(false);
                }}
              >
                {s.isBaseline && (
                  <span className="op-tab__lock" aria-hidden="true">
                    🔒
                  </span>
                )}
                {editingScenarioId === s.id ? (
                  <input
                    ref={renameInputRef}
                    className="op-tab-rename"
                    value={editingName}
                    maxLength={80}
                    aria-label="Scenario name"
                    onChange={(e) => setEditingName(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onBlur={() => commitRenameScenario(s.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        void commitRenameScenario(s.id);
                      }
                      if (e.key === 'Escape') {
                        e.preventDefault();
                        cancelRenameScenario();
                      }
                    }}
                  />
                ) : (
                  <span
                    className="op-tab__name"
                    onDoubleClick={(e) => startRenameScenario(s, e)}
                    title={s.isBaseline ? undefined : 'Double-click to rename'}
                  >
                    {s.name}
                  </span>
                )}
              </button>
            ))}
            <button type="button" className="op-tab op-tab--add" onClick={handleAddScenario}>
              + Add Scenario
            </button>
          </div>
        </div>

        <div className="op-compare-row">
          <span className="op-compare-row__label">Select to compare</span>
          {playground.scenarios.map((s) => (
            <label key={`cmp-${s.id}`} className="op-compare-check">
              <input
                type="checkbox"
                checked={compareIds.includes(s.id)}
                onChange={() => toggleCompareId(s.id)}
              />
              <span>{s.name}</span>
            </label>
          ))}
        </div>

        {compareMode && compareRows.length > 0 ? (
          <div className="op-panel op-compare-panel">
            <div className="op-panel__head">
              <div>
                <h2 className="op-panel__title">Scenario comparison</h2>
                <p className="op-panel__subtitle">Side-by-side metrics for up to 4 scenarios</p>
              </div>
              <button type="button" className="op-btn op-btn--ghost" onClick={() => setCompareMode(false)}>
                Back to editor
              </button>
            </div>
            <div className="op-compare-table-wrap">
              <table className="op-compare-table">
                <thead>
                  <tr>
                    <th>Metric</th>
                    {compareRows.map((r) => (
                      <th key={r.id}>{r.name}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(
                    [
                      ['Offer price', (r: OfferScenario) => money(r.inputs.purchasePrice)],
                      ['Monthly rent', (r: OfferScenario) => money(r.inputs.monthlyRent)],
                      ['NOI (annual)', (r: OfferScenario) => money(r.results?.noiAnnual)],
                      ['Cap rate', (r: OfferScenario) => pct(r.results?.capRate)],
                      ['Gross yield', (r: OfferScenario) => pct(r.results?.grossYield)],
                      ['Cash flow (annual)', (r: OfferScenario) => money(r.results?.cashFlowAnnual)],
                      ['Cash-on-cash', (r: OfferScenario) => pct(r.results?.cashOnCashReturn)],
                      [
                        'DSCR',
                        (r: OfferScenario) => (r.results?.dscr != null ? String(r.results.dscr) : '—'),
                      ],
                      ['Cash required', (r: OfferScenario) => money(r.results?.cashRequired)],
                    ] as Array<[string, (r: OfferScenario) => string]>
                  ).map(([label, fn]) => (
                    <tr key={label}>
                      <td>{label}</td>
                      {compareRows.map((r) => (
                        <td key={r.id}>{fn(r)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}

        {activeScenario && !compareMode && (
          <div className="op-grid">
            <div className="op-panel op-inputs">
              <div className="op-panel__head">
                <div>
                  <h2 className="op-panel__title">Assumptions / Inputs</h2>
                  <p className="op-panel__subtitle">
                    {readOnly
                      ? 'WTI Baseline is read-only — switch to My Offer or duplicate to edit.'
                      : 'Edit offer terms — results update live and autosave.'}
                  </p>
                </div>
              </div>

              <section className="op-section">
                <h3 className="op-section__title">
                  <span className="op-section__dot" />
                  Purchase
                </h3>
                <div className="op-wti-value">
                  <span>WTI estimated value</span>
                  <strong>{money(wtiValue)}</strong>
                </div>
                <Field
                  label="My offer / purchase price"
                  hint={
                    pctVsWti != null ? (
                      <span
                        className={`op-delta${
                          pctVsWtiNum != null && pctVsWtiNum > 0
                            ? ' op-delta--neg'
                            : pctVsWtiNum != null && pctVsWtiNum < 0
                              ? ' op-delta--pos'
                              : ''
                        }`}
                      >
                        {Number(pctVsWti) >= 0 ? '+' : ''}
                        {pctVsWti}% vs WTI value
                      </span>
                    ) : undefined
                  }
                >
                  <input
                    className="op-input op-input--lg"
                    type="number"
                    disabled={readOnly}
                    value={activeScenario.inputs.purchasePrice ?? ''}
                    onChange={(e) => patchInputs({ purchasePrice: numInput(e.target.value) })}
                  />
                </Field>
              </section>

              <section className="op-section">
                <h3 className="op-section__title">
                  <span className="op-section__dot" />
                  Income
                </h3>
                <div className="op-fields">
                  <Field label="Monthly rent">
                    <input
                      className="op-input"
                      type="number"
                      disabled={readOnly}
                      value={activeScenario.inputs.monthlyRent ?? ''}
                      onChange={(e) => patchInputs({ monthlyRent: numInput(e.target.value) })}
                    />
                  </Field>
                  <Field label="Other monthly income">
                    <input
                      className="op-input"
                      type="number"
                      disabled={readOnly}
                      value={activeScenario.inputs.monthlyOtherIncome ?? 0}
                      onChange={(e) => patchInputs({ monthlyOtherIncome: numInput(e.target.value) })}
                    />
                  </Field>
                </div>
              </section>

              <section className="op-section">
                <h3 className="op-section__title">
                  <span className="op-section__dot" />
                  Operating expenses
                </h3>
                <p className="op-section__hint">
                  Matches the cash flow pro forma — edit amount and basis; totals update as you type.
                </p>
                <div className="op-expense-grid">
                  <div className="op-expense-grid__head">
                    <span>Expense</span>
                    <span>Amount</span>
                    <span>Basis</span>
                    <span>Calculated</span>
                  </div>
                  {OPERATING_EXPENSE_FIELDS.map(({ key, label }) => {
                    const config = getExpenseConfig(key);
                    const line = cf?.expenseLines?.find((l) => l.key === key);
                    return (
                      <div key={key} className="op-expense-row">
                        <span className="op-expense-row__label">{label}</span>
                        <input
                          className="op-input"
                          type="number"
                          step="0.01"
                          disabled={readOnly}
                          value={config.value ?? ''}
                          onChange={(e) => patchExpense(key, { value: numInput(e.target.value) })}
                        />
                        <select
                          className="op-input op-select"
                          disabled={readOnly}
                          value={config.basis}
                          onChange={(e) =>
                            patchExpense(key, { basis: e.target.value as ExpenseBasis })
                          }
                        >
                          {EXPENSE_BASIS_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                        <span className="op-expense-row__calc">
                          {line ? moneyPerMonthYear(line.monthly, line.annual) : '—'}
                        </span>
                      </div>
                    );
                  })}
                  {customExpenses.map((row) => {
                    const rowId = row.id || row.label;
                    const line = cf?.expenseLines?.find(
                      (l) => l.key === `custom_${rowId}` || l.label === row.label,
                    );
                    return (
                      <div key={rowId} className="op-expense-row op-expense-row--custom">
                        <input
                          className="op-input"
                          type="text"
                          disabled={readOnly}
                          value={row.label}
                          placeholder="Expense name"
                          onChange={(e) =>
                            patchCustomExpense(rowId, { label: e.target.value })
                          }
                          onBlur={(e) => {
                            if (!e.target.value.trim()) {
                              patchCustomExpense(rowId, { label: 'Custom expense' });
                            }
                          }}
                        />
                        <input
                          className="op-input"
                          type="number"
                          step="0.01"
                          disabled={readOnly}
                          value={row.value ?? ''}
                          onChange={(e) =>
                            patchCustomExpense(rowId, { value: numInput(e.target.value) })
                          }
                        />
                        <select
                          className="op-input op-select"
                          disabled={readOnly}
                          value={row.basis || 'FIXED_MONTHLY'}
                          onChange={(e) =>
                            patchCustomExpense(rowId, {
                              basis: e.target.value as ExpenseBasis,
                            })
                          }
                        >
                          {EXPENSE_BASIS_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                        <span className="op-expense-row__calc">
                          {line ? moneyPerMonthYear(line.monthly, line.annual) : '—'}
                        </span>
                        {!readOnly && (
                          <button
                            type="button"
                            className="op-btn op-btn--ghost op-expense-remove"
                            title="Remove expense"
                            onClick={() => removeCustomExpense(rowId)}
                          >
                            ×
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
                {!readOnly && (
                  <button
                    type="button"
                    className="op-btn op-btn--ghost op-add-expense"
                    onClick={addCustomExpense}
                  >
                    + Add expense
                  </button>
                )}
              </section>

              <section className="op-section">
                <h3 className="op-section__title">
                  <span className="op-section__dot" />
                  Acquisition costs
                </h3>
                <div className="op-fields">
                  <Field label="Immediate repairs / CapEx">
                    <input
                      className="op-input"
                      type="number"
                      disabled={readOnly}
                      value={activeScenario.inputs.immediateRepairs ?? 0}
                      onChange={(e) => patchInputs({ immediateRepairs: numInput(e.target.value) })}
                    />
                  </Field>
                  <Field label="Other acquisition costs">
                    <input
                      className="op-input"
                      type="number"
                      disabled={readOnly}
                      value={activeScenario.inputs.otherAcquisitionCosts ?? 0}
                      onChange={(e) => patchInputs({ otherAcquisitionCosts: numInput(e.target.value) })}
                    />
                  </Field>
                </div>
                <div className="op-fields" style={{ marginTop: 12 }}>
                  <Field label="Closing costs amount">
                    <input
                      className="op-input"
                      type="number"
                      disabled={readOnly}
                      value={activeScenario.inputs.closingCosts?.value ?? 0}
                      onChange={(e) =>
                        patchInputs({
                          closingCosts: {
                            value: numInput(e.target.value),
                            basis:
                              activeScenario.inputs.closingCosts?.basis ?? 'FIXED_ANNUAL',
                          },
                        })
                      }
                    />
                  </Field>
                  <Field label="Closing costs basis">
                    <select
                      className="op-input op-select"
                      disabled={readOnly}
                      value={activeScenario.inputs.closingCosts?.basis ?? 'FIXED_ANNUAL'}
                      onChange={(e) =>
                        patchInputs({
                          closingCosts: {
                            value: activeScenario.inputs.closingCosts?.value ?? 0,
                            basis: e.target.value as ExpenseBasis,
                          },
                        })
                      }
                    >
                      {EXPENSE_BASIS_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>
              </section>

              <section className="op-section">
                <h3 className="op-section__title">
                  <span className="op-section__dot" />
                  Primary financing
                </h3>
                <div className="op-fields op-fields--3">
                  <Field label="Down payment (%)">
                    <input
                      className="op-input"
                      type="number"
                      disabled={readOnly}
                      value={activeScenario.inputs.primaryFinancing?.downPayment?.value ?? 20}
                      onChange={(e) =>
                        patchPrimaryFinancing({
                          downPayment: {
                            value: numInput(e.target.value),
                            basis: 'PERCENT_PURCHASE_PRICE',
                          },
                        })
                      }
                    />
                  </Field>
                  <Field label="Interest rate (%)">
                    <input
                      className="op-input"
                      type="number"
                      step="0.01"
                      disabled={readOnly}
                      value={activeScenario.inputs.primaryFinancing?.interestRate ?? 7}
                      onChange={(e) =>
                        patchPrimaryFinancing({ interestRate: numInput(e.target.value) })
                      }
                    />
                  </Field>
                  <Field label="Loan term (years)">
                    <input
                      className="op-input"
                      type="number"
                      disabled={readOnly}
                      value={activeScenario.inputs.primaryFinancing?.termYears ?? 30}
                      onChange={(e) =>
                        patchPrimaryFinancing({ termYears: numInput(e.target.value) })
                      }
                    />
                  </Field>
                </div>
                <div className="op-fields" style={{ marginTop: 12 }}>
                  <Field label="Amortized or Interest-Only">
                    <select
                      className="op-input op-select"
                      disabled={readOnly}
                      value={
                        activeScenario.inputs.primaryFinancing?.loanType || 'FULLY_AMORTIZING'
                      }
                      onChange={(e) =>
                        patchPrimaryFinancing({ loanType: e.target.value as LoanType })
                      }
                    >
                      {LOAN_TYPE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>
              </section>

              <section className="op-section">
                <h3 className="op-section__title">
                  <span className="op-section__dot" />
                  Secondary financing
                </h3>
                <p className="op-section__hint">
                  Optional 2nd loan (seller finance, hard money, HELOC). Keeps the 1st loan at purchase −
                  down payment, and subtracts the 2nd loan from cash required.
                </p>
                <label className="op-toggle">
                  <input
                    type="checkbox"
                    disabled={readOnly}
                    checked={!!activeScenario.inputs.secondaryFinancing?.enabled}
                    onChange={(e) => patchSecondaryFinancing({ enabled: e.target.checked })}
                  />
                  <span>Include secondary financing</span>
                </label>
                {!!activeScenario.inputs.secondaryFinancing?.enabled && (
                  <>
                    <div className="op-fields" style={{ marginTop: 12 }}>
                      <Field label="Label" wide>
                        <input
                          className="op-input"
                          type="text"
                          disabled={readOnly}
                          value={
                            activeScenario.inputs.secondaryFinancing?.label || 'Secondary financing'
                          }
                          onChange={(e) => patchSecondaryFinancing({ label: e.target.value })}
                        />
                      </Field>
                    </div>
                    <div className="op-fields op-fields--3" style={{ marginTop: 12 }}>
                      <Field label="Loan amount">
                        <input
                          className="op-input"
                          type="number"
                          step="0.01"
                          disabled={readOnly}
                          value={activeScenario.inputs.secondaryFinancing?.loanAmount?.value ?? 0}
                          onChange={(e) =>
                            patchSecondaryFinancing({
                              loanAmount: {
                                value: numInput(e.target.value),
                                basis:
                                  activeScenario.inputs.secondaryFinancing?.loanAmount?.basis ||
                                  'PERCENT_PURCHASE_PRICE',
                              },
                            })
                          }
                        />
                      </Field>
                      <Field label="Amount basis">
                        <select
                          className="op-input op-select"
                          disabled={readOnly}
                          value={
                            activeScenario.inputs.secondaryFinancing?.loanAmount?.basis ||
                            'PERCENT_PURCHASE_PRICE'
                          }
                          onChange={(e) =>
                            patchSecondaryFinancing({
                              loanAmount: {
                                value:
                                  activeScenario.inputs.secondaryFinancing?.loanAmount?.value ?? 0,
                                basis: e.target.value as ExpenseBasis,
                              },
                            })
                          }
                        >
                          <option value="PERCENT_PURCHASE_PRICE">% of purchase price</option>
                          <option value="FIXED_ANNUAL">Fixed $</option>
                        </select>
                      </Field>
                      <Field label="Interest rate (%)">
                        <input
                          className="op-input"
                          type="number"
                          step="0.01"
                          disabled={readOnly}
                          value={activeScenario.inputs.secondaryFinancing?.interestRate ?? 8}
                          onChange={(e) =>
                            patchSecondaryFinancing({ interestRate: numInput(e.target.value) })
                          }
                        />
                      </Field>
                    </div>
                    <div className="op-fields op-fields--3" style={{ marginTop: 12 }}>
                      <Field label="Loan term (years)">
                        <input
                          className="op-input"
                          type="number"
                          disabled={readOnly}
                          value={activeScenario.inputs.secondaryFinancing?.termYears ?? 10}
                          onChange={(e) =>
                            patchSecondaryFinancing({ termYears: numInput(e.target.value) })
                          }
                        />
                      </Field>
                      <Field label="Amortized or Interest-Only">
                        <select
                          className="op-input op-select"
                          disabled={readOnly}
                          value={
                            activeScenario.inputs.secondaryFinancing?.loanType ||
                            'FULLY_AMORTIZING'
                          }
                          onChange={(e) =>
                            patchSecondaryFinancing({ loanType: e.target.value as LoanType })
                          }
                        >
                          {LOAN_TYPE_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </Field>
                    </div>
                  </>
                )}
              </section>

              {!readOnly && (
                <div className="op-scenario-actions">
                  <button type="button" className="op-btn op-btn--ghost" onClick={handleResetScenario}>
                    Reset to WTI Baseline
                  </button>
                  <button type="button" className="op-btn op-btn--ghost" onClick={handleDuplicateScenario}>
                    Duplicate scenario
                  </button>
                  <button type="button" className="op-btn op-btn--danger" onClick={handleDeleteScenario}>
                    Delete scenario
                  </button>
                </div>
              )}
            </div>

            <div className="op-results-col">
              <div className="op-panel op-results">
                <div className="op-panel__head">
                  <div>
                    <h2 className="op-panel__title">Results / Analysis</h2>
                    <p className="op-panel__subtitle">Recalculated from your current assumptions</p>
                  </div>
                </div>
                <div className="op-metrics">
                  {metrics.map((m) => (
                    <div
                      key={m.label}
                      className={`op-metric${m.accent ? ' op-metric--accent' : ''} op-metric--${m.tone}`}
                    >
                      <div className="op-metric__label">{m.label}</div>
                      <div className="op-metric__value">{m.value}</div>
                    </div>
                  ))}
                </div>
                {cf?.mortgage && (
                  <div className="op-loan-line">
                    1st loan <strong>{money(cf.mortgage.loanAmount)}</strong> · {cf.mortgage.interestRate}% ·{' '}
                    {cf.mortgage.loanType === 'INTEREST_ONLY' ? 'IO' : 'amortizing'} ·{' '}
                    <strong>{money(cf.mortgage.monthlyPayment)}</strong>/mo
                    {cf.secondaryMortgage && cf.secondaryMortgage.loanAmount > 0 && (
                      <>
                        <br />
                        2nd loan <strong>{money(cf.secondaryMortgage.loanAmount)}</strong> ·{' '}
                        {cf.secondaryMortgage.interestRate}% ·{' '}
                        {cf.secondaryMortgage.loanType === 'INTEREST_ONLY' ? 'IO' : 'amortizing'} ·{' '}
                        <strong>{money(cf.secondaryMortgage.monthlyPayment)}</strong>/mo
                        {cf.totalDebtServiceMonthly != null && (
                          <>
                            {' '}
                            · Total debt <strong>{money(cf.totalDebtServiceMonthly)}</strong>/mo
                          </>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>

              {cf && (
                <div className="op-panel op-proforma">
                  <div className="op-panel__head">
                    <div>
                      <h2 className="op-panel__title">Cash flow pro forma</h2>
                      <p className="op-panel__subtitle">Operating expenses and bottom-line cash flow</p>
                    </div>
                  </div>
                  <CashFlowProformaTable cf={cf} />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OfferPlaygroundPage;
